import { TimeInput } from '../components/DateTimeInputs';
import { activeLang, useT } from '../lib/i18n';
import type { T } from '@bingo/shared';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { NavLink, useParams, Navigate } from 'react-router';
import { RESOURCES, RESOURCE_ORDER, type FieldSpec, type RefItem, type ResourceName, type ResourceSpec } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useRefData } from '../lib/reference';

const PAGE = 100;

/** Text shown for a value in the table. */
function display(f: FieldSpec, v: unknown, byId: ReturnType<typeof useRefData>['byId'], t: T): string {
  if (v === null || v === undefined || v === '') return '';
  switch (f.type) {
    case 'bool':
      return v ? t('Yes') : t('No');
    case 'enum':
      { const label = f.options?.find((o) => o.value === v)?.label; return label ? t(label) : String(v); }
    case 'ref': {
      const item = byId(f.ref!, v);
      return item ? String(item[RESOURCES[f.ref!].titleField] ?? '') : '';
    }
    case 'tags':
      return (v as string[]).join(', ');
    default:
      return String(v);
  }
}

function emptyForm(spec: ResourceSpec): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of spec.fields) out[f.name] = f.default ?? (f.type === 'bool' ? false : f.type === 'tags' ? [] : '');
  return out;
}

/** Free typing; the list is cut on commas, semicolons or spaces only for saving. */
function TagsInput({ id, value, disabled, onChange }: { id: string; value: string[]; disabled: boolean; onChange: (v: string[]) => void }) {
  const [text, setText] = useState(value.join(', '));
  const t = useT();
  return (
    <input id={id} className="input" value={text} disabled={disabled} placeholder={t('Separate with commas or spaces')}
      onChange={(e) => { setText(e.target.value); onChange(e.target.value.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean)); }} />
  );
}

function Field({ f, value, onChange, disabled }: { f: FieldSpec; value: unknown; onChange: (v: unknown) => void; disabled: boolean }) {
  const { lists } = useRefData();
  const t = useT();
  const id = `f-${f.name}`;
  let input;
  switch (f.type) {
    case 'longtext':
      input = <textarea id={id} className="input textarea" rows={3} value={String(value ?? '')} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'int':
      input = <input id={id} className="input" type="number" inputMode="numeric" min={f.min} max={f.max} value={value === null || value === '' ? '' : Number(value)}
        disabled={disabled} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} />;
      break;
    case 'bool':
      input = (
        <label className="check">
          <input id={id} type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
          <span>{value ? t('Yes') : t('No')}</span>
        </label>
      );
      break;
    case 'time':
      input = <TimeInput id={id} value={String(value ?? '')} disabled={disabled} onChange={(v) => onChange(v)} />;
      break;
    case 'color':
      input = <input id={id} className="input color" type="color" value={String(value || '#0f5fa8')} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
      break;
    case 'enum':
      input = (
        <select id={id} className="select" value={String(value ?? '')} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          {!f.required && <option value="">{t('None')}</option>}
          {f.options!.map((o) => <option key={o.value} value={o.value}>{t(o.label)}</option>)}
        </select>
      );
      break;
    case 'ref': {
      const target = RESOURCES[f.ref!];
      const items = (lists?.[f.ref!] ?? []).filter((x) => x.active || x.id === value);
      input = (
        <select id={id} className="select" value={String(value ?? '')} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          <option value="">{f.required ? t('Choose…') : t('None')}</option>
          {items.map((x) => (
            <option key={x.id} value={x.id}>
              {String(x[target.titleField])}{target.titleField !== 'name' && x.name ? ` · ${String(x.name)}` : ''}
            </option>
          ))}
        </select>
      );
      break;
    }
    case 'tags':
      input = <TagsInput id={id} value={(value as string[]) ?? []} disabled={disabled} onChange={onChange} />;
      break;
    default:
      input = <input id={id} className="input" value={String(value ?? '')} maxLength={f.maxLength} disabled={disabled}
        style={f.upper ? { textTransform: 'uppercase' } : undefined} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <div className="field">
      <label htmlFor={id}>{t(f.label)}{f.required && <span className="req"> *</span>}</label>
      {input}
      {f.help && <span className="hint">{t(f.help)}</span>}
    </div>
  );
}

interface HistoryRow { at: string; action: string; by: string; changes: Record<string, unknown> }

function Editor({ spec, item, onClose }: { spec: ResourceSpec; item: RefItem | null; onClose: () => void }) {
  const { me } = useAuth();
  const { put } = useRefData();
  const isDirection = me?.user.role === 'direction';
  const canEdit = isDirection || spec.officeCanCreate;
  const [form, setForm] = useState<Record<string, unknown>>(() => (item ? { ...item } : emptyForm(spec)));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  const t = useT();

  useEffect(() => {
    if (!item) return;
    api<{ history: HistoryRow[] }>(`/api/ref/${spec.name}/${item.id}/history`).then((r) => setHistory(r.history)).catch(() => setHistory([]));
  }, [item, spec.name]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const body: Record<string, unknown> = {};
      for (const f of spec.fields) {
        const v = form[f.name];
        if (item && JSON.stringify(v ?? null) === JSON.stringify(item[f.name] ?? null)) continue;
        body[f.name] = v === '' ? null : v;
        if (!item && (v === '' || v === null)) delete body[f.name];
      }
      const r = item
        ? await api<{ item: RefItem }>(`/api/ref/${spec.name}/${item.id}`, { method: 'PATCH', body: { ...body, version: item.version } })
        : await api<{ item: RefItem }>(`/api/ref/${spec.name}`, { method: 'POST', body });
      put(spec.name, r.item);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  async function setActive(active: boolean) {
    if (!item) return;
    setBusy(true);
    setError('');
    try {
      const r = await api<{ item: RefItem }>(`/api/ref/${spec.name}/${item.id}`, { method: 'PATCH', body: { version: item.version, active } });
      put(spec.name, r.item);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('Could not save.'));
    } finally {
      setBusy(false);
    }
  }

  const title = item ? String(item[spec.titleField] ?? '') : t(`New ${spec.singular}`);

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet card" onClick={(e) => e.stopPropagation()} onSubmit={save} aria-label={title}>
        <div className="sheet-head">
          <h2>{title}</h2>
          {item && !item.active && <span className="badge badge-bad">{t('Inactive')}</span>}
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>{t('Close')}</button>
        </div>
        <div className="sheet-body">
          {error && <div className="alert alert-bad" role="alert">{error}</div>}
          {!canEdit && <div className="alert alert-warn">{t('Only the direction can change this list.')}</div>}
          {spec.fields.map((f) => (
            <Field key={f.name} f={f} value={form[f.name]} disabled={!canEdit || busy} onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))} />
          ))}
          {item && (
            <div className="history">
              <h3>{t('History')}</h3>
              {history === null ? <span className="muted">{t('Loading…')}</span> : history.length === 0 ? (
                <span className="muted">{t('Imported from 2026, not changed since.')}</span>
              ) : (
                <ul>
                  {history.map((h, i) => (
                    <li key={i}>
                      <span className="muted">{new Date(h.at).toLocaleString(activeLang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' })}</span>{' '}
                      <b>{h.by}</b> {t(h.action)}
                      {h.action === 'update' && ': ' + Object.keys(h.changes).map((k) => t(spec.fields.find((f) => f.name === k)?.label ?? k)).join(', ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="sheet-foot">
          {canEdit && <button className="btn btn-primary" disabled={busy}>{item ? t('Save') : t('Add')}</button>}
          {item && isDirection && item.active && (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setActive(false)}>{t('Deactivate')}</button>
          )}
          {item && isDirection && !item.active && (
            <button type="button" className="btn" disabled={busy} onClick={() => setActive(true)}>{t('Reactivate')}</button>
          )}
        </div>
      </form>
    </div>
  );
}

function ResourceView({ spec }: { spec: ResourceSpec }) {
  const { me } = useAuth();
  const { lists, byId } = useRefData();
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const [editing, setEditing] = useState<RefItem | 'new' | null>(null);
  const canAdd = me?.user.role === 'direction' || spec.officeCanCreate;
  const columns = spec.fields.filter((f) => f.inTable);
  const t = useT();

  useEffect(() => {
    setQuery('');
    setLimit(PAGE);
    setShowInactive(false);
  }, [spec.name]);

  const rows = useMemo(() => {
    const all = lists?.[spec.name] ?? [];
    const q = query.trim().toLowerCase();
    return all.filter((x) => (showInactive || x.active) && (!q || columns.some((f) => display(f, x[f.name], byId, t).toLowerCase().includes(q))));
  }, [lists, spec.name, query, showInactive, columns, byId]);

  const inactiveCount = (lists?.[spec.name] ?? []).filter((x) => !x.active).length;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>{t(spec.label)}</h1>
          <div className="muted" style={{ fontSize: 14 }}>{t(spec.description)}</div>
        </div>
        {canAdd && <button className="btn btn-primary" onClick={() => setEditing('new')}>{t(`Add ${spec.singular}`)}</button>}
      </div>
      <div className="toolbar">
        <input className="input" type="search" placeholder={t('Search the list')} value={query}
          onChange={(e) => { setQuery(e.target.value); setLimit(PAGE); }} aria-label={t('Search')} />
        {inactiveCount > 0 && (
          <label className="check">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span>{t('Show inactive ({n})', { n: inactiveCount })}</span>
          </label>
        )}
        <span className="muted" style={{ marginLeft: 'auto', fontSize: 14 }}>{t('{n} shown', { n: rows.length })}</span>
      </div>
      <div className="card">
        <table className="table table-click">
          <thead><tr>{columns.map((f) => <th key={f.name}>{t(f.label)}</th>)}</tr></thead>
          <tbody>
            {rows.slice(0, limit).map((x) => (
              <tr key={x.id} className={x.active ? '' : 'inactive'} tabIndex={0}
                onClick={() => setEditing(x)} onKeyDown={(e) => e.key === 'Enter' && setEditing(x)}>
                {columns.map((f, i) => (
                  <td key={f.name} data-label={t(f.label)} className={i > 0 && !display(f, x[f.name], byId, t) && f.type !== 'color' ? 'empty' : undefined}>
                    {f.type === 'color' ? <span className="swatch" style={{ background: String(x[f.name]) }} /> : null}
                    {i === 0 ? <b>{display(f, x[f.name], byId, t)}</b> : display(f, x[f.name], byId, t)}
                    {i === 0 && !x.active && <span className="badge badge-bad" style={{ marginLeft: 8 }}>{t('Inactive')}</span>}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="muted">{t('Nothing found.')}</td></tr>}
          </tbody>
        </table>
      </div>
      {rows.length > limit && <div><button className="btn" onClick={() => setLimit(limit + PAGE)}>{t('Show {n} more', { n: Math.min(PAGE, rows.length - limit) })}</button></div>}
      {editing && <Editor key={editing === 'new' ? 'new' : editing.id} spec={spec} item={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

export function Settings() {
  const { resource } = useParams();
  const { lists, error } = useRefData();
  const t = useT();
  if (!resource) return <Navigate to="/settings/tours" replace />;
  const spec = RESOURCES[resource as ResourceName];
  if (!spec) return <Navigate to="/settings/tours" replace />;

  return (
    <div className="settings">
      <nav className="subnav" aria-label={t('Lists')}>
        {RESOURCE_ORDER.map((name) => (
          <NavLink key={name} to={`/settings/${name}`} className={({ isActive }) => (isActive ? 'active' : '')}>
            {t(RESOURCES[name].label)}
            <span className="count">{lists ? lists[name].filter((x) => x.active).length : ''}</span>
          </NavLink>
        ))}
      </nav>
      <div className="settings-main">
        {error && <div className="alert alert-bad">{error}</div>}
        {!lists ? <div className="muted">{t('Loading…')}</div> : <ResourceView spec={spec} />}
      </div>
    </div>
  );
}
